import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { motion, useDragControls, useMotionValue } from "motion/react";
import { X, Info, Sparkles, ChevronDown, ChevronRight, Eraser, Plus } from "lucide-react";
import { SpaceInfo } from "../../types/plan";
import { setCopiedSpaceData } from "../SideBarLeftPanel/SideBarLeftPanel.component";
import {
  TableCellsIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import cadviewer from "cadviewer";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import useConfig from "../../demos/hooks/useConfig";
import SelectedSpacesDrawer from "../../modules/ai/components/AIChatDrawer/components/SelectedSpacesDrawer";
import QuickActionsMenu from "../../modules/ai/components/AIChatDrawer/components/QuickActionsMenu";
import EventSystem from "../../events/EventSystem";
import { useSpaceToolsStore } from "../SpaceTools/stores/spaceToolsStore";
import {
  getSelectedSpacesData,
  calculateFinancialSummary,
  calculateOpportunityCost,
  generateQuickActionMessage,
} from "../../modules/ai/components/AIChatDrawer/utils/quickActionCalculations";
import {
  TableData,
  formatCellValue,
  exportTableAsCSV,
  exportTableAsXLSX,
  copyTableForExcel,
} from "../../modules/ai/types/table";
import { JsonEditorModal } from "../JsonEditorModal/JsonEditorModal";
import { VizQuerySetupModal } from "../VizQuerySetupModal/VizQuerySetupModal";
import { ApplicationSetupModal } from "../ApplicationSetupModal/ApplicationSetupModal";
import { AISetupModal } from "../AISetupModal/AISetupModal";
import { FolderSelectModal } from "../FolderSelectModal/FolderSelectModal";
import { ConfigureInformationLayersModal } from "../InformationLayersSetup/ConfigureInformationLayersModal";

// Position styles mapping
/**
 * Panels that borrow this window to host a setup form. They have nothing to do
 * with picking spaces on the drawing, so the selection chrome — the shift-click
 * tip, the action bar, the "select a space" empty state — stays out of their
 * way. Listing them once is what stops a new one from inheriting half of it.
 */
const SETUP_PANELS = [
  'processSourceDWG',
  'jsonDataExtraction',
  'vizQuerySetup',
  'applicationSetup',
  'aiSetup',
  'informationLayersSetup',
];
const isSetupPanel = (panel: string) => SETUP_PANELS.includes(panel);

const positionStyles: Record<string, string> = {
  "bottom-left": "absolute top-0 right-0 z-[9999]",
  "bottom-right": "absolute top-0 right-0 z-[9999]",
  "top-right": "absolute top-0 right-0 z-[9999]",
};

// 11.5.8     // 12.10.10
// All command-line parameters from ParseCommandLine.cpp (including aliases)
const rawAXparameters = [
  // update 2026-08-25
  "STSZ", "STX", "STY", "RP",
  "ND", "ID", "INPUTDIRECTORY", "OD",
  "OUTPUTDIRECTORY", "JS", "SN", "SNAP",
  "SM", "AL", "KL", "POLYLAYER",
  "POLYLAYER2", "POLYDIM", "IGNOREATTRIBUTES", "RA",
  "DG", "IP", "*RWD", "*RHT",
  "*RSZ", "*RDPI", "*RCC", "*RFC",
  "*RQL", "RBE", "*RBE", "RTE",
  "*RTE", "RBP", "*RBP", "RDM",
  "*RDM", "REM", "*REM", "RMD",
  "*RMD", "RMM", "*RMM", "RMT",
  "*RMT", "RTF", "*RTF", "A",
  "DIN0", "DIN1", "DIN2", "DIN3",
  "DIN4", "DIN5", "USA", "USB",
  "USC", "USD", "USE", "LETTER",
  "LEGAL", "TABLOID", "LEDGER", "PTS",
  "ARCHA", "ARCH_A", "ARCHB", "ARCH_B",
  "ARCHC", "ARCH_C", "ARCHD", "ARCH_D",
  "ARCHE", "ARCH_E", "GS", "COLORASC",
  "COLORASR", "BRIGHT", "PW", "LWTEXT",
  "POLYLINE_WIDTH_SCALE", "PWS", "ENHANCELW", "ENHANCEITEMSLW",
  "ELW", "ENHANCE", "ENHANCEITEMS", "EI",
  "LS", "DWFPDFTEXT", "DPT", "RO",
  "TA", "OL", "SB", "SF",
  "LOGOS", "IN", "INCH", "INCHES",
  "FT", "FOOT", "FEET", "M",
  "METER", "METERS", "METRE", "METRES",
  "MILE", "MILES", "YARD", "YARDS",
  "SMELTER", "BA1", "SCRAPER", "CDE-BASIC",
  "CREATEDUMMYENTITIES", "CREATEDUMMYENTITIES-BASIC", "CREATEDEMOENTITIES", "CREATEDEMOENTITIES-BASIC",
  "CREATEURBANEDGEDUMMY", "CUE", "MEGADEMO", "MEGAOFFICE",
  "MEGAOFFICEDEMO", "MOD", "NORLFILES", "NRF",
  "PLANIMETRON", "PPM", "PPM2", "UICDATA",
  "UICDRAWING", "UIC", "UICSCAN", "TD",
  "BLDGNUMBER", "COMMONAREA", "OUTOFBOUNDS", "OOB",
  "CA", "DL", "LOD_INCLUDESHORT", "LR",
  "RENTABLEAREA", "RN", "SHELL", "SHELLFINDER",
  "SRL", "STL", "TLEXCLUSIONS", "TLEX",
  "MALL", "BX", "CX", "DS",
  "NW", "CQ", "RE", "TS",
  "NPH", "SHOWINSERT", "NOBLOCKEX", "SU",
  "HP", "HIRES", "HIGHRES", "TOL",
  "SVGTOL", "RV", "NOHYPER", "NO_HYPER",
  "NOUNINIT", "NUI", "ON", "OPACITY",
  "OP", "MAPBLOCK", "MAP_BLOCK", "MB",
  "LT", "LAYERTABLE", "LS", "MLT",
  "MTS", "LIST", "MAGNIFY",


  "POLYDIMDIS","I", "O", "STAMP", "CSV", "XPATH", "XREFPATH", "FPATH", "FONTPATH",
  "SPATH", "SERVERPATH", "WKSP", "WORKSPACE", "RPATH", "RASTERPATH",
  "LPATH", "LICENSEPATH", "LICPATH", "REDLINE", "RED", "DATABASE", "DB", "WM",
  "NODEMAP", "LOD", "LD", "F", "SVG", "SVGZ", "SSVG", "SIMPLESVG", "RSVG", "RSVGZ",
  "PDF", "RPDF", "DWF", "DWG", "DXF", "DXFB", "JPG", "JPEG", "JPG0", "PNG", "GIF",
  "TIF", "TIFF", "CCITT", "CAL", "CALS", "G4", "BMP", "TXT", "TEXT", "EXT", "EXTENTS",
  "AI", "EPS", "EPSV", "EPSR", "PS", "CGM", "HPGL", "PLT", "PGL", "HPP", "HPGL2", "HP2", "HGL",
  "IGES", "IGS", "SVF", "SWF", "FLASH", "WMF", "WMFR", "JSR", "FAX", "WINFAX",
  "IOCA", "IOCAMO", "PSD", "TGA", "SUN", "WPG", "PCT", "PICT", "GEM", "MPNT", "PCX", "AWD", "CADKEY",
  "VIEW", "VN", "LAY", "LAYOUT", "SP", "ALL", "LAST", "MODEL", "PAPER", "MODELONLY", "BASIC",
  "ALLPAPER", "ALLMODEL", "LAYOUTSONLY", "ONLYLAYOUTS", "FIRSTLAYOUT", "FIRSTSHEET", "FIRST",
  "VIEWSONLY", "ONLYVIEWS", "NOLAST", "LOFF", "LAYER_OFF", "LAYERS_OFF", "LON", "LAYER_ON", "LAYERS_ON",
  "PON", "PART_ON", "PARTON", "POFF", "PART_OFF", "PARTOFF", "HATCHLAYER", "HATCHLAYERS", "HL",
  "TEXTLAYER", "TEXTLAYERS", "TL", "BLOCKLAYER", "BLOCKLAYERS", "BL", "ALTTEXTLAYER", "ALTTEXTLAYERS", "ALTTEXT", "AT",
  "ROOMLAYER", "ROOMLAYERS", "RL", "EXTRAAREA", "EXTRAAREALAYER", "EXTRAAREALAYERS", "EA",
  "HANDLELAYER", "HANDLELAYERS", "HLAYERS", "HLALL", "SNAPLAYER", "SNAPLAYERS", "SLAYERS", "SL",
  "DIMENSIONLAYER", "SHOWALL", "SHOWALLLAYERS", "SAL", "ALLLAYERS", "INCLUDEALL", "INCLUDEALLLAYERS", "IAL",
  "BLOCKS", "NOBLOCKS", "BLOCKNAME", "BN", "BLOCKNAMELAYER", "BNL", "ATTRIBUTEKEY", "AK",
  "TAG", "TG", "TLTAG", "BNA", "ATTRIBUTENAME", "BATTR", "BLANKATTRIBUTES", "BA",
  "INVISIBLE_ATTRIBUTES", "ALL_ATTRIBUTES", "IA", "AA", "IB", "BO", "IGNOREBLOCK", "IGNOREBLOCKS", "IGNORE_BLOCK", "IGNORE_BLOCKS",
  "LINK_BLOCK", "LINK_BLOCKS", "LB", "BLOCKTOBLACK", "B2B", "SOLIDHATCH", "SH",
  "RWD", "WIDTH", "RHT", "HEIGHT", "RSZ", "SIZE", "DRAWSZ", "DPI", "RDPI", "RCC", "RFC", "ROR", "RQL",
  "MARGIN", "MA", "A0", "A1", "A2", "A3", "A4", "A5", "UA", "UB", "UC", "UD", "UE", "DIN", "ANSI", "ARCH", "ALCOA",
  "B", "BAC", "BGD", "BACKGROUND", "BLACK", "BK", "WHITE", "WB", "BW", "MONO", "BLACKWHITE", "FORCEBLACK",
  "GRAY", "GREY", "GRAYSCALE", "GREYSCALE", "COLORASCSS", "COLORASRGB", "OUTPUTCOLORCODE", "OUTPUTCOLORCODES", "OCC",
  "BRIGHTNESS", "BR", "CTB", "LW", "LWS", "LINEWIDTHSCALE", "LWMIN", "ML", "MINIMUMLINEWIDTH", "LWTMIN",
  "LTD", "LINETYPEDIVISOR", "LTS", "LINETYPESCALE", "CW", "COLORWIDTHTABLE", "MAXDASH", "MD", "MAXGAP", "MG",
  "FONT", "DEFAULTFONT", "DF", "ITALIC", "DEFAULTITALICFONT", "DI", "DEFAULTS", "DEFAULTB", "FSMIN", "FS",
  "TEXTONLY", "NOTEXTSCALE", "NOTS", "STROKE", "ST", "STROKETEXT", "STROKEALLSHX", "STROKEA", "STROKEALL",
  "KEEPDGNTEXT", "KEEPD", "T2M", "CREATEBLOCKS", "TLPOSTFIX", "TLPREFIX", "TLPRE", "MAXSTRINGLENGTH", "MSL", "MAXSTRING",
  "LANGUAGE", "LAN", "ENGLISH", "ENG", "EN", "UK", "SPANISH", "SP", "ES", "GERMAN", "GER", "DEUTCH", "DE", "GE",
  "FRENCH", "FR", "CHINESE", "CHIN", "CH", "JAPANESE", "JAP", "JA", "JP", "KOREAN", "KOR", "KO", "KR", "HEBREW", "HEB", "HE",
  "CODEPAGE", "CP", "NOEXTENTS", "ORNT", "R", "ORIENTATION", "LANDSCAPE", "PORTRAIT", "ROT", "ROTATION",
  "AUTOTRIM", "AU", "CROP", "CROPT", "CROPB", "CROPL", "CROPR", "CROP_T", "CROP_B", "CROP_L", "CROP_R",
  "WATERMARK", "WM_CONTENT", "WM_CONTENTS", "WM_CON", "WM_SIZE", "WM_HEIGHT", "WM_ANGLE", "WM_X", "WM_XPOS",
  "WM_Y", "WM_YPOS", "WM_FONT", "WM_TEXTSTYLE", "WM_STYLE", "WM_ANCHOR", "WM_ANC", "WM_LAYOUT", "WM_LAY",
  "WM_COLOR", "WM_COL", "WM_RGB", "WATERMARKONBOTTOM", "CLEAN", "RASTER", "EMBED", "NOEMBED", "MIDPOINT",
  "HANDLEBASED", "HB", "LISTAREA", "LA", "BREAKBYLAYER", "XL", "UNIFIED", "SPLITX", "COMPARE", "COMPAREATTR",
  "SAMESIZE", "SAMESI", "TREATAS", "TREATRGB", "HATCH", "HT", "HATCHSCALE", "HS", "NOHATCH", "DOMINION", "DOM",
  "SVGBACKGROUND", "SVGFOREGROUND", "LOGO", "UNIT", "UNITS", "U", "US", "UN", "CM", "MM", "UP", "MP", "XP",
  "V", "VER", "VERSION", "?", "HIS", "HISTORY", "VERBOSE", "TRACE", "DEBUG", "DEBUGLEVEL", "FLOW", "Q", "QUIET", "LOG",
  "REGEX", "RX", "MYSQL", "MSPORT", "MSUSER", "MSPWD", "MSUSERN", "MSEMAIL", "MSORG", "MSUSERT", "MSDATA", "MSDRAW",
  "MSLAYE", "MSLAYO", "MSTEXT", "MSBL", "MSATT", "MINER", "DU1", "DU2", "DU4", "DX1", "DX2", "DX3", "DX4", "DX4S", "DX5",
  "DXLIST", "BOA", "EUROP", "META", "STATS", "EXPORT", "CDE", "SAMPLEDATA", "SAMPLE_DATA", "SA", "KD", "BLDG", "BUILDING",
  "FLR", "FLOOR", "THRESHOLDDISTANCE", "THRESHOLD", "LOD_ROOMID", "LODID", "OUTPUTDISTANCES", "ODD", "ODJ",
  "BLOCKINGLINES", "CORRIDORLINES", "DIMENSIONSTRING", "DENY", "AUTHOR", "SUBJECT", "KEYWORDS", "TITLE",
  "PREC", "PRECISION", "HIGH", "MEDIUM", "LOW", "NETWORK", "SAVEDXF", "SAVEDWG", "SUB", "SUBDIRS", "QUANT",
  "REPO", "REPOSITORY", "OVERWRITE", "OM", "TOOLTIP", "TOOLT", "TT", "CHECKCLOSED", "CHECKC", "SHORT", "JSUNICODE",
  "POSTFIX", "NOALIAS", "THUMBSIZE", "UNICODEFILENAME", "UFN", "NOPLACEHOLDER", "EFR", "LICENSE", "LICENCE",
  "PAGENUMBER", "PG", "RASTERFORMAT", "RFORMAT", "RFMT", "POINTCIRCLE", "PTC", "SHOWINSERTIONPOINT", "SIP",
  "NOBLOCKEXTENTS", "NBE", "CREATEPLACEHOLDER", "STRICTURL", "STRICTHYPERLINK", "STRICT", "JSON", "ZIP", "Z",
  "WMDATA", "DEMO", "NORMALIZE"
];
const AXparameters = rawAXparameters.filter((item, pos) => rawAXparameters.indexOf(item) === pos).sort((a, b) => a.localeCompare(b));

// Result Modal Component
interface ResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  tableData: TableData | null;
}

const ResultModal: React.FC<ResultModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  tableData,
}) => {
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // Drag and resize state
  const [modalWidth, setModalWidth] = useState(1200);
  const [modalHeight, setModalHeight] = useState(700);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const initialMousePos = useRef({ x: 0, y: 0 });
  const initialSize = useRef({ width: 0, height: 0 });
  const initialPosition = useRef({ x: 0, y: 0 });

  // Motion values for position
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  // Drag controls
  const dragControls = useDragControls();

  // Resize handlers - MUST be before any conditional returns
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeDirection) return;

      const deltaX = e.clientX - initialMousePos.current.x;
      const deltaY = e.clientY - initialMousePos.current.y;

      let newWidth = modalWidth;
      let newHeight = modalHeight;
      let newX = x.get();
      let newY = y.get();

      // Handle horizontal resizing
      if (resizeDirection.includes('left')) {
        newWidth = initialSize.current.width - deltaX;
        newX = initialPosition.current.x + deltaX;
      } else if (resizeDirection.includes('right')) {
        newWidth = initialSize.current.width + deltaX;
      }

      // Handle vertical resizing
      if (resizeDirection.includes('top')) {
        newHeight = initialSize.current.height - deltaY;
        newY = initialPosition.current.y + deltaY;
      } else if (resizeDirection.includes('bottom')) {
        newHeight = initialSize.current.height + deltaY;
      }

      // Apply max constraints only (no min constraints)
      newWidth = Math.min(newWidth, window.innerWidth - 40);
      newHeight = Math.min(newHeight, window.innerHeight - 40);

      setModalWidth(newWidth);
      setModalHeight(newHeight);

      if (resizeDirection.includes('left') || resizeDirection.includes('top')) {
        x.set(newX);
        y.set(newY);
      }
    };

    const handleMouseUp = () => {
      setResizeDirection(null);
    };

    if (resizeDirection) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizeDirection, modalWidth, modalHeight, x, y]);

  // Reset position when modal opens
  useEffect(() => {
    if (isOpen) {
      x.set(0);
      y.set(0);
    }
  }, [isOpen, x, y]);

  // Drag constraints - allow dragging to all screen edges
  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  // Early returns AFTER all hooks
  if (!tableData || !isOpen) return null;

  const { labels, data } = tableData;
  const fieldNames = Object.keys(labels);

  // Handle CSV export
  const handleExportCSV = () => {
    const csvContent = exportTableAsCSV(tableData);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const filename = `${title.replace(/\s+/g, "_")}_export.csv`;
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle Excel export as native .xlsx file
  const handleExportExcel = async () => {
    const filename = `${title.replace(/\s+/g, "_")}_export.xlsx`;
    await exportTableAsXLSX(tableData, filename);
  };

  // Handle copy to clipboard for Excel paste
  const handleCopyForExcel = async () => {
    try {
      const tsvContent = copyTableForExcel(tableData);
      await navigator.clipboard.writeText(tsvContent);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      alert("Failed to copy to clipboard. Please try again.");
    }
  };

  // Handle resize start
  const handleResizeStart = (direction: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setResizeDirection(direction);
    initialMousePos.current = { x: e.clientX, y: e.clientY };
    initialSize.current = { width: modalWidth, height: modalHeight };
    initialPosition.current = { x: x.get(), y: y.get() };
  };

  return (
    <div className="fixed inset-0 z-[1000000003] w-full h-full" style={{ pointerEvents: 'none' }}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" style={{ pointerEvents: 'none' }}>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        {/* Modal - now draggable and resizable */}
        <motion.div
          ref={modalRef}
          drag
          dragListener={false}
          dragControls={dragControls}
          dragConstraints={dragConstraints}
          dragElastic={0}
          dragMomentum={false}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginLeft: -(modalWidth / 2),
            marginTop: -(modalHeight / 2),
            x,
            y,
            width: modalWidth,
            height: modalHeight,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40,
            pointerEvents: 'auto',
          }}
          className="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 relative flex flex-col"
        >
          {/* Resize Handles - 4 sides */}

          {/* Top */}
          <div
            onMouseDown={(e) => handleResizeStart('top', e)}
            style={{
              position: 'absolute',
              left: 8,
              right: 8,
              top: 0,
              height: 8,
              cursor: 'ns-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal height from top"
          />

          {/* Right */}
          <div
            onMouseDown={(e) => handleResizeStart('right', e)}
            style={{
              position: 'absolute',
              right: 0,
              top: 8,
              bottom: 8,
              width: 8,
              cursor: 'ew-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal width from right"
          />

          {/* Bottom */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom', e)}
            style={{
              position: 'absolute',
              left: 8,
              right: 8,
              bottom: 0,
              height: 8,
              cursor: 'ns-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal height from bottom"
          />

          {/* Left */}
          <div
            onMouseDown={(e) => handleResizeStart('left', e)}
            style={{
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: 8,
              cursor: 'ew-resize',
              zIndex: 10,
            }}
            aria-label="Resize modal width from left"
          />

          {/* Resize Handles - 4 corners */}

          {/* Top-Left Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('top-left', e)}
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from top-left corner"
          />

          {/* Top-Right Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('top-right', e)}
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 16,
              height: 16,
              cursor: 'nesw-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from top-right corner"
          />

          {/* Bottom-Left Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom-left', e)}
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              width: 16,
              height: 16,
              cursor: 'nesw-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from bottom-left corner"
          />

          {/* Bottom-Right Corner */}
          <div
            onMouseDown={(e) => handleResizeStart('bottom-right', e)}
            style={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              zIndex: 11,
            }}
            aria-label="Resize modal from bottom-right corner"
          />

          {/* Header - Draggable - Fixed */}
          <div
            className="flex-none px-6 py-4 border-b cursor-move select-none"
            style={{
              background: "linear-gradient(to right, #0f3557, #0a2338)",
              borderBottomColor: "#072033",
              userSelect: "none",
            }}
            onPointerDown={(e) => dragControls.start(e)}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-xl leading-6 font-bold text-white flex items-center gap-2">
                <TableCellsIcon className="h-6 w-6" />
                {title}
              </h3>
              <div className="flex items-center gap-2">
                {/* Export buttons */}
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Export as CSV"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  CSV
                </button>
                <button
                  onClick={handleExportExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Export as Excel"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Excel
                </button>
                <button
                  onClick={handleCopyForExcel}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-md text-sm font-medium transition-colors"
                  title="Copy for Excel paste"
                >
                  {copiedToClipboard ? (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <ClipboardDocumentIcon className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="text-white transition-colors ml-2"
                  style={{ opacity: 0.9 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "0.9";
                  }}
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            <p
              className="mt-1 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {message}
            </p>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
            <div className="px-6 py-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      {fieldNames.map((fieldName) => (
                        <th
                          key={fieldName}
                          className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider border-r border-gray-200 bg-gray-50"
                        >
                          {labels[fieldName]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={
                          row._is_summary
                            ? "font-semibold"
                            : "hover:bg-gray-50"
                        }
                        style={row._is_summary ? { backgroundColor: 'rgba(15, 53, 87, 0.1)' } : {}}
                      >
                        {fieldNames.map((fieldName) => {
                          const value = row[fieldName];
                          const formattedValue = formatCellValue(
                            fieldName,
                            value
                          );

                          return (
                            <td
                              key={fieldName}
                              className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200"
                            >
                              {formattedValue}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="flex-none bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div className="text-xs text-gray-500">
              {copiedToClipboard && (
                <span className="text-green-600 font-medium">
                  ✓ Copied to clipboard! You can now paste into Excel.
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto sm:text-sm"
              style={{ backgroundColor: "#0f3557" }}
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const SpaceSelectionPanel: React.FC = () => {
  const { config } = useConfig();
  const { 
    multiSelectArray, 
    setMultiSelectArray,
    plansInfo,
    selectedPlan,
    selectedPlanDetail,
    currentDrawingPath,
    currentDrawingVersion,
    currentCanvasInstance,
    user
  } = useContext(CadviewerContext);

  // Space Tools Store
  const { 
    activePanel, 
    closePanel, 
    lodFolder,
    lodSpaceLayer,
    setLodSpaceLayer,
    lodTextLayer,
    setLodTextLayer,
    selectedLayers,
    processSpaceLayer,
    setProcessSpaceLayer,
    processTextLayer,
    setProcessTextLayer,
    processParameters,
    setProcessParameters,
    jsonParameters,
    setJsonParameters
  } = useSpaceToolsStore();

  // Only render if this panel is active
  const isOpen = ['financialAnalysis', 'opportunityCost', 'lod', 'spaceAssociation', ...SETUP_PANELS].includes(activePanel);

  // State
  const [selectedSpacesChips, setSelectedSpacesChips] = useState<string[]>([]);
  const [spaceIdInputValue, setSpaceIdInputValue] = useState("");
  const [otherSpacesInputValue, setOtherSpacesInputValue] = useState("VACANT;VACANT RETAIL");
  const [minDimensionDistance, setMinDimensionDistance] = useState("");
  const [layerOffList, setLayerOffList] = useState("");
  const [contentLine1, setContentLine1] = useState("");
  const [contentLine2, setContentLine2] = useState("");
  const [contentLine3, setContentLine3] = useState("");
  const [dbSearchKeyword, setDbSearchKeyword] = useState("");
  const [selectedDBEntry, setSelectedDBEntry] = useState<SpaceInfo | null>(null);

  // Json Data Extraction state
  const [isJsonDataEditorOpen, setIsJsonDataEditorOpen] = useState(false);
  const [extractedJsonData, setExtractedJsonData] = useState<string>("");
  const [extractedJsonFilename, setExtractedJsonFilename] = useState<string>("");

  // Space Association workflow state
  const [drawingSpaceData, setDrawingSpaceData] = useState<{ id: string; name: string; type: string; area: number } | null>(null);
  const [importedData, setImportedData] = useState<{ id: string; name: string; type: string; area: number } | null>(null);
  const [areaUnit, setAreaUnit] = useState<"in" | "ft">("in");
  const [localPlansInfo, setLocalPlansInfo] = useState<any | null>(null);

  const [dbSource, setDbSource] = useState<string>("VizQuery");
  const [isJsonFileSelectOpen, setIsJsonFileSelectOpen] = useState(false);
  const [folderStructure, setFolderStructure] = useState<any[]>([]);
  const [selectedJsonFileUrl, setSelectedJsonFileUrl] = useState<string>("");

  const fetchFolderStructure = React.useCallback(async () => {
    try {
      let endpoint = "listdwgdirectory";
      if (!config.ServerBackEndUrl.endsWith("/")) endpoint = "/" + endpoint;

      const headers = new Headers();
      if (user?.token && config.enableLoginFeature) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }
      headers.append("Content-Type", "application/json");
      headers.append("Accept", "application/json");

      const response = await fetch(
        config.ServerBackEndUrl + endpoint + `?loginFeatureEnabled=${config.enableLoginFeature}`,
        { method: "GET", headers }
      );
      if (response.ok) {
        const data = await response.json();
        setFolderStructure(data);
      }
    } catch (error) {
      console.error("Failed to load folder structure:", error);
    }
  }, [config.ServerBackEndUrl, user?.token, config.enableLoginFeature]);

  useEffect(() => {
    if (activePanel === 'spaceAssociation') {
      fetchFolderStructure();
    }
  }, [activePanel, fetchFolderStructure]);

  const fetchLocalJson = async (rawPath: string) => {
    try {
      let normalizedPath = rawPath.replace(/\\/g, "/");
      if (!normalizedPath.startsWith("content/")) {
        normalizedPath = "content/" + (normalizedPath.startsWith("/") ? normalizedPath.substring(1) : normalizedPath);
      }
      
      let endpoint = `settings/folders/versions/file?path=${encodeURIComponent(normalizedPath)}`;
      const headers = new Headers();
      headers.append("Accept", "application/json");
      if (user?.token) headers.append("Authorization", `Bearer ${user.token}`);
      
      const response = await fetch(config.ServerBackEndUrl + (config.ServerBackEndUrl.endsWith("/") ? "" : "/") + endpoint, {
        method: "GET",
        headers
      });
      if (response.ok) {
        const text = await response.text();
        const data = JSON.parse(text);
        const plansData = data.data ? data.data : data;
        if (plansData) {
          setLocalPlansInfo(plansData);
        }
      } else {
        console.error("Failed to fetch JSON file:", response.status);
      }
    } catch (e) {
      console.error("Error fetching local json:", e);
    }
  };

  const filteredFolderStructure = useMemo(() => {
    if (!config.serverSubFolder) return folderStructure;

    const normalizedServerSubFolder = config.serverSubFolder.endsWith("/")
      ? config.serverSubFolder.slice(0, -1)
      : config.serverSubFolder;

    const targetFolderName = config.serverSubFolder
      .split("/")
      .filter(Boolean)
      .pop();

    const normPath = (p: string) => p.replace(/\\/g, "/");
    const findByPath = (folderList: any[]): any | null => {
      for (const folder of folderList) {
        if (folder.path && normPath(folder.path).endsWith(normalizedServerSubFolder)) {
          return folder;
        }
        if (folder.children) {
          const found = findByPath(folder.children);
          if (found) return found;
        }
      }
      return null;
    };
    const findByName = (folderList: any[]): any | null => {
      for (const folder of folderList) {
        if (folder.name === targetFolderName) return folder;
        if (folder.children) {
          const found = findByName(folder.children);
          if (found) return found;
        }
      }
      return null;
    };

    const targetFolder = findByPath(folderStructure) ?? findByName(folderStructure);
    return targetFolder?.children || [];
  }, [folderStructure, config.serverSubFolder]);

  const filterDBSpaceInfos = useMemo(() => {
    const currentPlansInfo = localPlansInfo || plansInfo;
    return (currentPlansInfo ?? { spaces: [] }).spaces.filter(
      (spaceInfo: SpaceInfo) => {
        return dbSearchKeyword.trim() === ""
          ? true
          : (spaceInfo["spaceNumber"] ?? "")
              .toLowerCase()
              .includes(dbSearchKeyword.toLowerCase()) ||
              (spaceInfo["spaceHeader"] ?? "")
                .toLowerCase()
                .includes(dbSearchKeyword.toLowerCase()) ||
              (spaceInfo.summary && Object.values(spaceInfo.summary).some((value: string) =>
                value.toLowerCase().includes(dbSearchKeyword.toLowerCase())
              ));
      }
    );
  }, [plansInfo, localPlansInfo, dbSearchKeyword]);

  useEffect(() => {
    setSpaceIdInputValue(prev => {
      const currentParsed = prev.split(',').map(s => s.trim()).filter(Boolean);
      const arraysEqual = currentParsed.length === selectedSpacesChips.length && 
        currentParsed.every((val, index) => val === selectedSpacesChips[index]);
      
      if (!arraysEqual) {
        return selectedSpacesChips.join(', ');
      }
      return prev;
    });

    // Space Association: fetch CADViewer metadata when a single space is selected
    if (activePanel === 'spaceAssociation') {
      if (selectedSpacesChips.length === 1) {
        const spaceId = selectedSpacesChips[0];
        try {
          // @ts-ignore
          const getName = cadviewer.cvjs_getSpaceObjectNamefromId || window.cvjs_getSpaceObjectNamefromId;
          const name = getName ? getName(spaceId) : "";
          // @ts-ignore
          const getType = cadviewer.cvjs_getSpaceObjectTypefromId || window.cvjs_getSpaceObjectTypefromId;
          const type = getType ? getType(spaceId) : "";
          // @ts-ignore
          const getArea = cadviewer.cvjs_getSpaceObjectAreafromId || window.cvjs_getSpaceObjectAreafromId;
          let area = getArea ? getArea(spaceId) : -1;
          
          if (typeof area === "string") {
            area = parseFloat(area) || -1;
          }
          
          setDrawingSpaceData({
            id: spaceId,
            name: name || "",
            type: type || "Space",
            area: area
          });
          setImportedData(null); // Reset import on new selection
        } catch (e) {
          console.error("Error fetching CADViewer space metadata:", e);
        }
      } else {
        setDrawingSpaceData(null);
        setImportedData(null);
      }
    }
  }, [selectedSpacesChips, activePanel]);
  const [dbAccordionState, setDbAccordionState] = useState<Record<string, boolean>>({
    "Tenant Overview": true,
    "Tenant & Space Info": false,
    "Lease Financials": false,
    "Lease Terms": false,
    "Important Dates": false,
    "Legal Triggers & Flags": false,
    "Sales Performance": false,
    "Others": false,
  });

  const toggleDbAccordion = (section: string) => {
    setDbAccordionState(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const [isCollapsed, setIsCollapsed] = useState(false); // For collapsing/expanding the widget
  
  const [actionStatus, setActionStatus] = useState<"idle" | "processing_dwg" | "generating_pdf">("idle");
  const [generatedPdf, setGeneratedPdf] = useState<{ url: string; fileName: string } | null>(null);
  const pendingPdfStateRef = useRef<{
    spaceId: string;
    pdfFileName: string;
    baseUrl: string;
    token?: string;
    lodFolder: string;
  } | null>(null);
  // Holds the PDF blob captured from jsPDF's client-side save
  const capturedPdfBlobRef = useRef<Blob | null>(null);

  const [resultModal, setResultModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    tableData: TableData | null;
  }>({
    isOpen: false,
    title: "",
    message: "",
    tableData: null,
  });

  // Drag controls for making the panel draggable
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const dragControls = useDragControls();

  // Drag constraints to keep panel within screen bounds
  const dragConstraints = useMemo(
    () => ({
      left: -(window.innerWidth - 100),
      right: window.innerWidth - 100,
      top: -(window.innerHeight - 100),
      bottom: window.innerHeight - 100,
    }),
    []
  );

  // Note: We do NOT automatically clear selectedSpacesChips when multiSelectArray becomes empty
  // because they are independent states:
  // - multiSelectArray: temporary selection from drag-select
  // - selectedSpacesChips: permanent user-saved selection

  // Automated Space Selection
  useEffect(() => {
    const handleAutoAddSpaces = (data: any) => {
      const spaces = data as string[];
      if (!spaces) return;
      const uniqueSpaces = Array.from(new Set(spaces));
      
      if (activePanel === 'lod' && uniqueSpaces.length > 1) {
        window.alert("Only one space used in LOD report");
      }
      
      setSelectedSpacesChips(uniqueSpaces);
    };

    EventSystem.subscribe("cadviewer.auto_add_spaces", handleAutoAddSpaces);
    
    return () => {
      EventSystem.unsubscribe("cadviewer.auto_add_spaces", handleAutoAddSpaces);
    };
  }, [activePanel]);

  const [layerNamesMap, setLayerNamesMap] = useState<Record<string, string>>({});

  useEffect(() => {
    if (activePanel === 'lod' || activePanel === 'processSourceDWG') {
      try {
        const layerTable = cadviewer.cvjs_getLayerTable(currentCanvasInstance || "floorPlan");
        if (layerTable && layerTable.layers) {
          const map: Record<string, string> = {};
          const offLayers: string[] = [];
          layerTable.layers.forEach((l: any) => {
            const id = l.layer || l.name || l.layerName;
            const name = l.layerName || l.layer || l.name;
            if (id) {
              map[id] = name;
            }
            if ((l.layerState === "Off" || l.status === "OFF" || l.status === "off") && name) {
              offLayers.push(name);
            }
          });
          setLayerNamesMap(map);
          if (activePanel === 'lod') {
            setLayerOffList(offLayers.join(";"));
          }
        }
      } catch (e) {
        console.error("Error fetching layers for mapping:", e);
      }
    }
  }, [activePanel, cadviewer, currentCanvasInstance]);

  const cvjs_processCompleted = React.useCallback(async (process: string) => {
    if (["cvjs_publishPDF", "cvjs_publishMultiPagePDF"].includes(process)) {
      if (!pendingPdfStateRef.current) return;
      
      const { spaceId, pdfFileName, baseUrl, token, lodFolder } = pendingPdfStateRef.current;
      
      try {
        console.log(`[LOD Trace] PDF generation completed. Retrieving captured PDF blob...`);
        
        // The PDF is generated client-side by jsPDF — we captured it via our saveAs intercept
        const pdfBlob = capturedPdfBlobRef.current;
        if (!pdfBlob) {
          console.error("[LOD Trace] No PDF blob was captured from jsPDF.");
          alert("PDF generation completed but the file could not be captured. Please try again.");
          return;
        }
        capturedPdfBlobRef.current = null;
        
        // Ensure folder exists before uploading PDF
        const fullUploadPath = await ensureFolderExists(baseUrl, lodFolder, spaceId, token);

        // Upload it to the LOD folder
        const pdfFile = new File([pdfBlob], pdfFileName, { type: "application/pdf" });
        const formData = new FormData();
        formData.append("files", pdfFile);
        formData.append("global", "true");
        
        formData.append("path", fullUploadPath);
        
        const uploadHeaders = new Headers();
        if (token) uploadHeaders.append("Authorization", `Bearer ${token}`);
        
        const uploadRes = await fetch(`${baseUrl}/settings/folders/upload`, {
          method: "POST",
          body: formData,
          headers: uploadHeaders,
        });
        
        if (uploadRes.ok) {
           // Provide a direct link for download using the Blob we already fetched successfully
           setGeneratedPdf({ url: URL.createObjectURL(pdfBlob), fileName: pdfFileName });
        } else {
           const errText = await uploadRes.text();
           console.error("[LOD Trace] Failed to upload PDF:", errText);
           alert(`Failed to upload PDF to LOD folder. Server said: ${errText}`);
        }
      } catch (e) {
        console.error("[LOD Trace] Error uploading generated PDF:", e);
      } finally {
        setActionStatus("idle");
        pendingPdfStateRef.current = null;
        
        // Restore view
        setTimeout(() => {
          cadviewer.cvjs_ClearDrawing(currentCanvasInstance ?? "floorPlan");
          cadviewer.cvjs_setZoomImageWallpaper(false);
          if (currentDrawingPath) {
             cadviewer.cvjs_LoadDrawing(currentCanvasInstance ?? "floorPlan", currentDrawingPath);
          }
        }, 500);
      }
    }
  }, [config, currentCanvasInstance, currentDrawingPath]);

  useEffect(() => {
    cadviewer.cvjs_setCallbackMethod("cvjs_processCompleted", cvjs_processCompleted);
  }, [cvjs_processCompleted]);

  const getLoadedFileName = (path?: string | null) => {
    if (!path) return "No file loaded";
    let fileName = path.split(/[/\\]/).pop() || "";
    try {
      fileName = decodeURIComponent(fileName);
    } catch (e) {
      // ignore if not valid URI component
    }
    const ext = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
    if (ext) {
      const firstExtIndex = fileName.toLowerCase().indexOf(ext.toLowerCase());
      if (firstExtIndex !== -1) {
        return fileName.substring(0, firstExtIndex + ext.length);
      }
    }
    return fileName;
  };

  const formatLayerName = (id: string) => {
    return id.startsWith('layer_') ? id.substring(6) : id;
  };

  const handleAddParameter = () => {
    setProcessParameters([...processParameters, { name: "", value: "" }]);
  };

  const handleUpdateParameter = (index: number, field: 'name' | 'value', val: string) => {
    const updated = [...processParameters];
    updated[index] = { ...updated[index], [field]: val };
    setProcessParameters(updated);
  };

  const handleRemoveParameter = (index: number) => {
    setProcessParameters(processParameters.filter((_, i) => i !== index));
  };

  const handleRunServerSideConversion = () => {
    if (!processSpaceLayer || typeof cadviewer === "undefined") return;

    cadviewer.cvjs_conversion_clearAXconversionParameters();

    /* push all conversion parameters as a single json object */
    const axparameters: any = {};
    const parameters: any[] = [];
    axparameters.parameters = parameters;

    const rlName = layerNamesMap[processSpaceLayer] || formatLayerName(processSpaceLayer);
    axparameters.parameters.push({ paramName: "RL", paramValue: rlName });
    
    if (processTextLayer && processTextLayer.trim() !== "") {
      const tlName = layerNamesMap[processTextLayer] || formatLayerName(processTextLayer);
      axparameters.parameters.push({ paramName: "TL", paramValue: tlName });
    }

    for (let i = 0; i < processParameters.length; i++) {
      const p = processParameters[i];
      if (p.name) {
        axparameters.parameters.push({
          paramName: p.name,
          paramValue: p.value || "",
        });
      }
    }

    console.log(JSON.stringify(axparameters));
    cadviewer.cvjs_conversion_addAXconversionParameters(axparameters);

    if (currentDrawingPath && currentCanvasInstance) {
      cadviewer.cvjs_LoadDrawing(currentCanvasInstance, currentDrawingPath);
    }
  };

    const handleAddJsonParameter = () => {
      setJsonParameters([...jsonParameters, { name: "", value: "" }]);
    };
  
    const handleUpdateJsonParameter = (index: number, field: 'name' | 'value', val: string) => {
      const updated = [...jsonParameters];
      updated[index] = { ...updated[index], [field]: val };
      setJsonParameters(updated);
    };
  
    const handleRemoveJsonParameter = (index: number) => {
      setJsonParameters(jsonParameters.filter((_, i) => i !== index));
    };

    const handleRunDataExtraction = async () => {
      if (!currentDrawingPath || typeof cadviewer === "undefined") {
        alert("No drawing loaded or Cadviewer not initialized.");
        return;
      }
      
      // Ensure there's a backend URL to call
      if (!config.ServerBackEndUrl) {
        alert("Server back end URL is not configured.");
        return;
      }

      try {
        setActionStatus("processing_dwg");
        
        const axparameters = [
          { paramName: "json", paramValue: "" }
        ];
        
        jsonParameters.forEach(p => {
          if (p.name) {
            axparameters.push({
              paramName: p.name,
              paramValue: p.value || ""
            });
          }
        });

        // Fix currentDrawingPath if it contains absolute path details instead of a clean URL
        let contentLoc = currentDrawingPath.replace(/\\/g, "/");
        const contentIndex = contentLoc.indexOf("content/");
        if (contentIndex !== -1) {
          contentLoc = contentLoc.substring(contentIndex);
          contentLoc = config.ServerBackEndUrl + (config.ServerBackEndUrl.endsWith('/') ? '' : '/') + contentLoc;
        } else if (!contentLoc.startsWith("http")) {
          // Remove leading slash if present to avoid double slashes after ServerBackEndUrl
          if (contentLoc.startsWith("/")) {
            contentLoc = contentLoc.substring(1);
          }
          contentLoc = config.ServerBackEndUrl + (config.ServerBackEndUrl.endsWith('/') ? '' : '/') + contentLoc;
        }

        const requestObj = {
          action: "data_extraction",
          converter: "AutoXchange AX2025",
          version: "V25.00",
          contentType: "file",
          contentResponse: "stream",
          contentFormat: "DWG",
          contentUsername: "",
          contentPassword: "",
          userLabel: "fromMyApp",
          leaveStreamOnServer: 0,
          contentLocation: contentLoc,
          parameters: axparameters
        };

        const formData = new URLSearchParams();
        formData.append("request", JSON.stringify(requestObj));
        
        const endpoint = config.ServerBackEndUrl + (config.ServerBackEndUrl.endsWith('/') ? '' : '/') + "callapiconversion";
        
        console.log("Calling JSON Data Extraction: ", endpoint, requestObj);
        
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded" // jQuery ajax uses this
          },
          body: formData.toString()
        });
        
        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }
        
        const textResponse = await response.text();
        let conversionResult = textResponse;
        
        try {
          const parsed = JSON.parse(textResponse);
          if (typeof parsed === "string" && parsed.trim().startsWith("http")) {
            // The server returned a JSON string that is just a URL
            const dataResponse = await fetch(parsed.trim());
            conversionResult = await dataResponse.text();
          } else if (parsed.contentStreamData) {
            if (typeof parsed.contentStreamData === "string" && parsed.contentStreamData.trim().startsWith("http")) {
              const dataResponse = await fetch(parsed.contentStreamData.trim());
              conversionResult = await dataResponse.text();
            } else {
              conversionResult = parsed.contentStreamData;
            }
          }
        } catch (e) {
          // If it's not JSON, maybe it's just a raw URL string
          if (typeof textResponse === "string" && textResponse.trim().startsWith("http")) {
            try {
              const dataResponse = await fetch(textResponse.trim());
              conversionResult = await dataResponse.text();
            } catch (fetchErr) {
              console.error("Failed to fetch JSON data from the returned URL:", fetchErr);
            }
          } else {
            console.warn("Response is not JSON and not a URL", e);
          }
        }

        setExtractedJsonData(conversionResult);
        let filename = currentDrawingPath.split(/[/\\]/).pop() || "extracted.json";
        try { filename = decodeURIComponent(filename); } catch(e) {}
        setExtractedJsonFilename(filename.replace(/\.dwg$/i, '.json'));
        setIsJsonDataEditorOpen(true);
        
      } catch (e) {
        console.error("JSON Data Extraction failed:", e);
        alert("JSON Data Extraction failed. See console for details.");
      } finally {
        setActionStatus("idle");
      }
    };

  const handleRemoveSpaceChip = (spaceId: string) => {
    setSelectedSpacesChips((prev) => prev.filter((id) => id !== spaceId));
  };

  const handleClearAllSpaceChips = () => {
    cadviewer.cvjs_clearSpaceLayer(); // Clear visual highlights on canvas
    cadviewer.cvjs_clearMultiSelectArrays(); // Clear CADViewer multiselect arrays
    setSelectedSpacesChips([]);
    setMultiSelectArray([]);
  };

  const getLodDisplayUrl = () => {
    if (!lodFolder) return "No LOD folder selected";
    let relativePath = lodFolder.replace(/\\/g, '/');
    if (relativePath.includes('/custom/')) {
      relativePath = relativePath.split('/custom/')[1];
    } else if (relativePath.includes('/content/')) {
      relativePath = relativePath.split('/content/')[1];
    } else {
      const parts = relativePath.split('/');
      relativePath = parts.slice(Math.max(0, parts.length - 2)).join('/');
    }
    return `${window.location.host}/${relativePath}`;
  };

  const getHeaderTitle = () => {
    switch (activePanel) {
      case 'financialAnalysis': return 'Space Selection & Financial Analysis';
      case 'opportunityCost': return 'Space Selection & Opportunity Cost';
      case 'lod': return 'Space Selection & LOD';
      case 'spaceAssociation': return 'Space Selection & Association';
      case 'processSourceDWG': return 'Process Source DWG';
      case 'jsonDataExtraction': return 'JSON Data Extraction';
      case 'vizQuerySetup': return 'VizQuery Folder Structure Setup';
      case 'applicationSetup': return 'VizQuery Application Setup';
      case 'aiSetup': return 'VizQuery AI Setup';
      case 'informationLayersSetup': return 'Configure Information Layers';
      default: return 'Space Selection';
    }
  };

  const ensureFolderExists = async (baseUrl: string, lodFolder: string, spaceId: string, token?: string | null) => {
    const folderHeaders = new Headers();
    folderHeaders.append("Content-Type", "application/json");
    if (token) folderHeaders.append("Authorization", `Bearer ${token}`);

    try {
      await fetch(`${baseUrl}/upload/folder`, {
        method: "POST",
        headers: folderHeaders,
        body: JSON.stringify({ folder_name: spaceId, path: `custom/${lodFolder}`, global: true })
      });
    } catch (e) {
      console.error("[LOD Trace] Folder creation error:", e);
    }
    
    return `custom/${lodFolder}/${spaceId}`;
  };

  const getActionTitle = () => {
    switch (activePanel) {
      case 'financialAnalysis': return 'Run Financial Analysis';
      case 'opportunityCost': return 'Run Opportunity Cost';
      case 'lod': return 'Run LOD';
      case 'spaceAssociation': return 'Associate Space';
      default: return 'Action';
    }
  };

  const handleActionClick = async () => {
    if (activePanel === 'lod') {
      if (!currentDrawingPath) {
        alert("Please load a drawing first.");
        return;
      }

      setActionStatus("processing_dwg");
      setGeneratedPdf(null);
      
      try {
        const ServerBackEndUrl = config.ServerBackEndUrl || "http://localhost:3000";
        const baseUrl = ServerBackEndUrl.endsWith("/") ? ServerBackEndUrl.slice(0, -1) : ServerBackEndUrl;
        const lodFolder = (config as any).LODFolder || "uedge.com/LOD";

        let lastContentStreamUrl1 = "";
        let lastContentStreamUrl2 = "";

        for (const spaceId of selectedSpacesChips) {
          // 1. Prepare conversion payload
          const payload: any = {
            action: "cadviewer_display",
            converter: "AutoXchange AX2024",
            version: "V23.00",
            contentType: "file",
            contentResponse: "stream",
            contentFormat: "DWG",
            contentUsername: "",
            contentPassword: "",
            userLabel: "fromMyApp",
            leaveStreamOnServer: 0,
            contentLocation: currentDrawingPath,
            parameters: [
              { paramName: "f", paramValue: "svg" },
              { paramName: "LOD", paramValue: "" },
              { paramName: "trace", paramValue: "" },
            ]
          };

          const actualSpaceLayer = lodSpaceLayer ? (layerNamesMap[lodSpaceLayer] || lodSpaceLayer) : "";
          if (actualSpaceLayer) {
            payload.parameters.push({ paramName: "DL", paramValue: actualSpaceLayer });
            payload.parameters.push({ paramName: "LODID", paramValue: spaceId });
          }

          if (lodTextLayer) {
            const actualTextLayer = layerNamesMap[lodTextLayer] || lodTextLayer;
            payload.parameters.push({ paramName: "TL", paramValue: actualTextLayer });
          }

          payload.parameters.push({ paramName: "POLYDIMDIS", paramValue: minDimensionDistance });

          if (layerOffList) {
            payload.parameters.push({ paramName: "LOFF", paramValue: layerOffList });
          }

          payload.parameters.push({ paramName: "EI", paramValue: otherSpacesInputValue });

          if (contentLine1) {
            payload.parameters.push({ paramName: "L1", paramValue: contentLine1 });
          }

          if (contentLine2) {
            payload.parameters.push({ paramName: "L2", paramValue: contentLine2 });
          }

          if (contentLine3) {
            payload.parameters.push({ paramName: "L3", paramValue: contentLine3 });
          }

          // 2. Call conversion API
          const res = await fetch(`${baseUrl}/callapiconversion`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ request: JSON.stringify(payload) })
          });

          if (!res.ok) throw new Error(`Server returned ${res.status}`);

          const rawText = await res.text();
          let safeJsonText = rawText;
          if (safeJsonText.startsWith("callapiconversion")) {
            safeJsonText = safeJsonText.substring("callapiconversion".length);
          }
          
          let conversionData;
          try {
            conversionData = JSON.parse(safeJsonText);
          } catch (err) {
            safeJsonText = safeJsonText.replace(/\\([^"\\])/g, '\\\\$1');
            conversionData = JSON.parse(safeJsonText);
          }
          
          const contentStreamDataRaw = conversionData.contentStreamData;
          if (!contentStreamDataRaw) continue;
          
          let contentStreamData = contentStreamDataRaw.replace("returnCadviewer=1", "returnCadviewer=0");
          // Keep files on server so PDF generation can access them
          if (!contentStreamData.includes("remainOnServer")) {
            contentStreamData += "&remainOnServer=1";
          }

          // Build the second URL with Layout fileTag
          const url2 = new URL(contentStreamData);
          let fileTag = url2.searchParams.get("fileTag");
          if (fileTag) {
            url2.searchParams.set("fileTag", fileTag + "_Layout_SITE LOD");
          }
          const contentStreamData2 = url2.toString();

          // Fetch SVGs for uploading copies to COLDSTONE folder
          const fileRes1 = await fetch(contentStreamData);
          const blob1 = await fileRes1.blob();

          const fileRes2 = await fetch(contentStreamData2);
          const blob2 = await fileRes2.blob();

          let drawingName = currentDrawingPath.split(/[/\\]/).pop() || "drawing";
          try { drawingName = decodeURIComponent(drawingName); } catch(e) {}
          const baseName = drawingName.substring(0, drawingName.lastIndexOf('.')) || drawingName;
          
          const file1 = new File([blob1], `${baseName}.svg`, { type: "image/svg+xml" });
          const file2 = new File([blob2], `${baseName}_Layout_SITE LOD.svg`, { type: "image/svg+xml" });

          const uploadHeaders = new Headers();
          const token = user?.token || localStorage.getItem("token");
          if (token) uploadHeaders.append("Authorization", `Bearer ${token}`);

          // Ensure folder exists recursively before uploading SVGs
          const fullUploadPath = await ensureFolderExists(baseUrl, lodFolder, spaceId, token);
          
          const formData = new FormData();
          formData.append("files", file1);
          formData.append("files", file2);
          formData.append("global", "true");
          
          formData.append("path", fullUploadPath);

          const svgUploadRes = await fetch(`${baseUrl}/settings/folders/upload`, {
            method: "POST",
            body: formData,
            headers: uploadHeaders,
          });
          
          if (!svgUploadRes.ok) {
            const errText = await svgUploadRes.text();
            console.error("[LOD Trace] SVG Upload failed:", errText);
            alert(`Failed to upload SVGs to LOD folder. Server said: ${errText}`);
          }

          // Save contentStreamData URLs for PDF generation
          lastContentStreamUrl1 = contentStreamData;
          lastContentStreamUrl2 = contentStreamData2;
        }
        
        // Check if we need to run PDF generation for the single selected space
        if (selectedSpacesChips.length === 1 && lastContentStreamUrl1 && lastContentStreamUrl2) {
          const spaceId = selectedSpacesChips[0];
          
          let drawingName = currentDrawingPath.split(/[/\\]/).pop() || "drawing";
          try { drawingName = decodeURIComponent(drawingName); } catch(e) {}
          const baseName = drawingName.substring(0, drawingName.lastIndexOf('.')) || drawingName;
          const f1 = lastContentStreamUrl1;
          const f2 = lastContentStreamUrl2;
          
          const safeBaseName = baseName.replace(/\s+/g, '_');
          const pdfFileName = `${safeBaseName}_LOD_${spaceId}.pdf`;
          
          pendingPdfStateRef.current = {
            spaceId,
            pdfFileName,
            baseUrl,
            token: user?.token || localStorage.getItem("token") || undefined,
            lodFolder
          };
          
          setActionStatus("generating_pdf");
          
          // Intercept jsPDF's browser download to capture the PDF blob.
          // jsPDF generates the PDF client-side and triggers a download via an anchor element.
          // It calls URL.createObjectURL(blob) internally. We intercept that to grab the blob.
          const originalCreateObjectURL = URL.createObjectURL.bind(URL);
          URL.createObjectURL = (obj: Blob | MediaSource): string => {
            // Check if this is a PDF blob (jsPDF produces application/pdf or similar)
            if (obj instanceof Blob && obj.size > 1000) {
              console.log(`[LOD Trace] Intercepted createObjectURL: size=${obj.size}, type=${obj.type}`);
              capturedPdfBlobRef.current = obj;
            }
            // Restore immediately
            URL.createObjectURL = originalCreateObjectURL;
            return originalCreateObjectURL(obj);
          };
          
          cadviewer.cvjs_setPrintOrientation("landscape");
          cadviewer.cvjs_publishMultiPagePDF(
             currentCanvasInstance ?? "floorPlan",
             "", "", "", "", [],
             [f1, f2],
             400, "letter", 1.0,
             pdfFileName
          );
        } else {
          alert("LOD extraction completed for selected spaces.");
          setActionStatus("idle");
        }
      } catch (error) {
        console.error("Error during LOD extraction:", error);
        alert("Error during LOD extraction. See console for details.");
        setActionStatus("idle");
      }
    } else if (activePanel === 'financialAnalysis') {
      handleTriggerAction('Financial Summary');
    } else if (activePanel === 'opportunityCost') {
      handleTriggerAction('Opportunity Cost');
    } else if (activePanel === 'spaceAssociation') {
      if (!selectedDBEntry) {
        alert("Please select a Data Base Entry first.");
        return;
      }
      if (selectedSpacesChips.length === 0) {
        alert("Please select at least one space to associate.");
        return;
      }
      console.log("Associating spaces:", selectedSpacesChips, "with entry:", selectedDBEntry);
      
      // Echo the data for CADViewer
      let dba = selectedDBEntry?.summary?.DBA || "";
      if (!dba && selectedDBEntry?.detailTabs) {
        const tabWithDba = selectedDBEntry.detailTabs.find((tab: any) => tab.detail?.DBA);
        if (tabWithDba) dba = tabWithDba.detail.DBA;
      }
      setCopiedSpaceData({ spaceNumber: selectedDBEntry.spaceNumber, DBA: dba });

      alert(`Ready to associate DB Entry ${selectedDBEntry.spaceNumber} (${selectedDBEntry.spaceHeader}) with spaces: ${selectedSpacesChips.join(', ')}`);
      // The actual CADViewer association method should be connected here
    }
  };

  const handleTriggerAction = (actionName: string) => {

    // Check if we have selected spaces
    if (selectedSpacesChips.length === 0) {
      alert("Please select at least one space to perform this analysis.");
      return;
    }

    // DEBUG: Log all the data to help troubleshoot
    console.log("=== DEBUG: Quick Action Triggered ===");
    console.log("1. Selected Space Chips:", selectedSpacesChips);
    console.log("2. plansInfo exists:", !!plansInfo);
    console.log("3. plansInfo:", plansInfo);
    if (plansInfo?.spaces) {
      console.log("4. Total spaces in plansInfo:", plansInfo.spaces.length);
      console.log("5. First 5 space numbers from plansInfo:",
        plansInfo.spaces.slice(0, 5).map(s => s.spaceNumber)
      );
      console.log("6. Sample space object:", plansInfo.spaces[0]);
    }

    // Get space data from plansInfo
    const spacesData = getSelectedSpacesData(plansInfo, selectedSpacesChips);

    console.log("7. Spaces data found:", spacesData.length);
    console.log("8. Spaces data:", spacesData);
    console.log("=====================================");

    if (spacesData.length === 0) {
      alert("Could not find data for the selected spaces. Please ensure a plan is loaded and try again.");
      return;
    }

    let tableData = null;
    let messageContent = "";
    let modalTitle = "";

    if (actionName === "Financial Summary") {
      tableData = calculateFinancialSummary(spacesData);
      messageContent = generateQuickActionMessage(
        "Financial Summary",
        spacesData.length,
        selectedPlan?.propertyName
      );
      modalTitle = "Financial Summary";
    } else if (actionName === "Opportunity Cost") {
      tableData = calculateOpportunityCost(spacesData);
      messageContent = generateQuickActionMessage(
        "Opportunity Cost",
        spacesData.length,
        selectedPlan?.propertyName
      );
      modalTitle = "Opportunity Cost Analysis";
    }

    if (tableData) {
      setResultModal({
        isOpen: true,
        title: modalTitle,
        message: messageContent,
        tableData,
      });
    }
  };

  const handleCloseResultModal = () => {
    setResultModal({
      isOpen: false,
      title: "",
      message: "",
      tableData: null,
    });
  };

  const positionClass = positionStyles[config.spaceSelectionPosition] || positionStyles["bottom-left"];
  const isActionEnabled = selectedSpacesChips.length > 0 && (activePanel === 'lod' ? !!lodFolder : true);

  // Don't render if panel is not open
  if (!isOpen) return null;

  return (
    <>
      {/* Space Selection Widget - Draggable */}
      <motion.div
        drag
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={dragConstraints}
        dragElastic={0}
        dragMomentum={false}
        style={{ x, y }}
        className={positionClass}
      >
        <div className="bg-white rounded-lg shadow-xl border-2 w-[450px]" style={{ borderColor: '#0f3557' }}>
          {/* Title Bar - Always visible with collapse/expand button - Draggable handle */}
          <div
            className="px-3 py-2 text-white flex items-center justify-between cursor-move rounded-t-md"
            style={{ background: 'linear-gradient(to right, #0f3557, #0a2a42)', userSelect: 'none' }}
            onPointerDown={(e) => dragControls.start(e)}
          >
            <h3 className="text-xs font-bold flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
              {getHeaderTitle()}
            </h3>
            <div className="flex items-center gap-1">
              <button
                onClick={closePanel}
                className="p-1 rounded transition-colors"
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#0a2a42'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Close"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          {/* Content - Hidden when collapsed */}
          {!isCollapsed && (
            <div className="flex flex-col">
              {/* Loaded File Banner for LOD and Space Association */}
              {(activePanel === 'lod' || activePanel === 'spaceAssociation') && (
                <div 
                  className="px-3 py-2 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition grid grid-cols-[auto_1fr] gap-x-2 gap-y-1.5 items-center shrink-0"
                  onClick={() => EventSystem.publish("panel.folder_list.open", {})}
                  title="Click to open Folder Structure"
                >
                  <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Loaded File:</span>
                  <div className="overflow-x-auto whitespace-nowrap flex items-center h-full" style={{ scrollbarWidth: 'none' }}>
                    <span className="text-[11px] font-medium text-slate-700">
                      {getLoadedFileName(currentDrawingPath)}
                    </span>
                  </div>
                  
                  <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Version:</span>
                  <div className="flex items-center">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      currentDrawingVersion === 'Current' || currentDrawingVersion === 'LATEST_FROM_TREE' || !currentDrawingVersion 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      {currentDrawingVersion === 'LATEST_FROM_TREE' ? 'Latest' : (currentDrawingVersion || 'Current')}
                    </span>
                  </div>
                </div>
              )}

              {/* Helpful Hint Banner */}
              {!isSetupPanel(activePanel) && (
                <div className="bg-slate-50/80 text-slate-500 px-3 py-1 text-[10.5px] flex items-center gap-1.5 border-b border-slate-100 w-full">
                  <Info size={12} className="text-indigo-400" />
                  <span><strong className="font-semibold text-slate-600">Tip:</strong> Use <strong>Shift+Click</strong> on the canvas to select, multi-select or deselect spaces.</span>
                </div>
              )}

              {!isSetupPanel(activePanel) && (
                <div className="flex flex-wrap items-center px-3 py-1.5 text-xs gap-1 bg-white">
                  <div className="flex items-center gap-2">
                    {activePanel === 'lod' && generatedPdf && (
                      <button
                        onClick={() => {
                          const a = document.createElement("a");
                          a.href = generatedPdf.url;
                          a.download = generatedPdf.fileName;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }}
                        className="flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm cursor-pointer"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        Download PDF
                      </button>
                    )}
                    {activePanel !== 'spaceAssociation' && (
                      <button
                        onClick={handleActionClick}
                        disabled={actionStatus !== "idle" || !isActionEnabled}
                        className={`flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border ${
                          (actionStatus === "idle" && isActionEnabled)
                            ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm cursor-pointer'
                            : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        {actionStatus !== "idle" ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-1 text-slate-400" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            {actionStatus === "processing_dwg" ? "Processing DWG..." : "Generating PDF..."}
                          </>
                        ) : (
                          <>
                            <Sparkles size={14} className={isActionEnabled ? 'text-indigo-200' : 'text-slate-300'} />
                            <span className="whitespace-nowrap">{getActionTitle()}</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <SelectedSpacesDrawer
                    selectedSpacesChips={selectedSpacesChips}
                    onRemoveSpaceChip={handleRemoveSpaceChip}
                    onClearAllSpaceChips={handleClearAllSpaceChips}
                    plansInfo={plansInfo}
                  />

                  <button
                    onClick={handleClearAllSpaceChips}
                    className="flex items-center gap-1.5 text-slate-600 hover:text-indigo-600 font-medium transition py-1 px-2 rounded-md hover:bg-slate-50"
                  >
                    <Eraser size={14} className="text-rose-500" />
                    <span className="whitespace-nowrap">
                      Clear Spaces
                    </span>
                  </button>
                </div>
              )}

              {activePanel === 'spaceAssociation' && (
                <div className="flex flex-col">
                  {/* Action Bar */}
                  <div className="flex flex-wrap items-center px-3 py-2 border-b border-slate-100 bg-white gap-2">
                    <button
                      onClick={() => EventSystem.publish("modal.save_svg.open", {})}
                      className="flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border text-xs border-indigo-600 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 shadow-sm cursor-pointer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                      <span className="whitespace-nowrap">Save as SVG</span>
                    </button>
                  </div>
                  
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex flex-col gap-3 text-xs overflow-y-auto custom-scrollbar-light flex-1 max-h-[60vh]">
                    {/* Database Source Selection */}
                    <div className="flex flex-col gap-1.5">
                      <label className="font-semibold text-slate-700 text-sm">Source</label>
                      <div className="flex gap-2 items-center">
                        <select
                          value={dbSource}
                          onChange={(e) => {
                            setDbSource(e.target.value);
                            if (e.target.value !== "VizQuery") {
                              setLocalPlansInfo(null);
                              setSelectedJsonFileUrl("");
                            }
                          }}
                          className="flex-1 bg-white border border-slate-300 rounded px-2 py-1.5 text-slate-700 outline-none focus:border-indigo-500"
                        >
                          <option value="VizQuery">VizQuery</option>
                          <option value="MRI">MRI</option>
                          <option value="SalesForce">SalesForce</option>
                          <option value="Snowflake">Snowflake</option>
                        </select>
                        {dbSource === "VizQuery" && (
                          <button
                            onClick={() => setIsJsonFileSelectOpen(true)}
                            className="flex items-center gap-1.5 font-medium transition py-1.5 px-3 rounded-md border text-xs border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 shadow-sm cursor-pointer whitespace-nowrap"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-1.2-1.8A2 2 0 0 0 8.55 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"></path></svg>
                            File
                          </button>
                        )}
                      </div>
                      {dbSource === "VizQuery" && selectedJsonFileUrl && (
                        <div className="text-[10px] text-slate-500 truncate mt-0.5">
                          {selectedJsonFileUrl}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                      <div className="font-semibold text-slate-700 text-sm">Select Data Base Entry</div>
                      {selectedDBEntry && (
                        <button
                        onClick={() => {
                          setSelectedDBEntry(null);
                          setCopiedSpaceData(null);
                        }}
                        className="text-xs text-rose-500 hover:text-rose-600 hover:bg-rose-50 px-2 py-1 rounded transition-colors"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    value={dbSearchKeyword}
                    onChange={(e) => setDbSearchKeyword(e.target.value)}
                    placeholder="Search database entries..."
                    className="w-full bg-white border border-slate-300 rounded px-2 py-1.5 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                  />
                  <div className="flex-1 overflow-y-auto border border-slate-200 rounded bg-white mt-1 custom-scrollbar-light min-h-[100px]">
                    {filterDBSpaceInfos.length === 0 ? (
                      <div className="p-3 text-slate-500 text-center italic">No entries found.</div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {filterDBSpaceInfos.map((info: any) => (
                          <label key={info.spaceNumber} className={`flex items-start gap-2 p-2 cursor-pointer hover:bg-indigo-50 transition ${selectedDBEntry?.spaceNumber === info.spaceNumber ? 'bg-indigo-50/50' : ''}`}>
                            <input
                              type="radio"
                              name="dbEntrySelection"
                              checked={selectedDBEntry?.spaceNumber === info.spaceNumber}
                              onChange={() => {
                                setSelectedDBEntry(info);
                                let dba = info?.summary?.DBA || "";
                                if (!dba && info?.detailTabs) {
                                  const tabWithDba = info.detailTabs.find((tab: any) => tab.detail?.DBA);
                                  if (tabWithDba) dba = tabWithDba.detail.DBA;
                                }
                                setCopiedSpaceData({ spaceNumber: info.spaceNumber, DBA: dba });
                              }}
                              className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                            />
                            <div className="flex flex-col flex-1 min-w-0">
                              <div className="font-medium text-slate-800 truncate">{info.spaceHeader}</div>
                              <div className="text-slate-500 text-[10px] truncate">ID: {info.spaceNumber}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Drawing Space Information UI */}
                  {drawingSpaceData && (
                    <div className="mt-2 border-t border-slate-200 pt-3">
                      <div className="flex justify-between items-center mb-1.5">
                        <div className="font-medium text-slate-700">Drawing Space Information</div>
                        <button
                          onClick={() => setAreaUnit(prev => prev === 'in' ? 'ft' : 'in')}
                          className="text-[10px] bg-slate-100 hover:bg-slate-200 text-slate-600 px-2 py-0.5 rounded transition"
                        >
                          Show in sq {areaUnit === 'in' ? 'ft' : 'inches'}
                        </button>
                      </div>
                      <div className="bg-white p-2 rounded border border-indigo-100 text-[11px] text-slate-600 flex flex-col gap-1 mb-2">
                        <div className="flex justify-between">
                          <span className="font-semibold text-slate-700">ID:</span>
                          <span>{drawingSpaceData.id}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-semibold text-slate-700">Name:</span>
                          <span>{drawingSpaceData.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-semibold text-slate-700">Type:</span>
                          <span>{drawingSpaceData.type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-semibold text-slate-700">Area:</span>
                          <span>
                            {drawingSpaceData.area === -1 ? 'N/A' : 
                              areaUnit === 'in' ? `${drawingSpaceData.area.toFixed(2)} sq in` : `${(drawingSpaceData.area / 144).toFixed(2)} sq ft`
                            }
                          </span>
                        </div>
                      </div>

                      {/* Staged Import Data block */}
                      {importedData && (
                        <div className="bg-indigo-50 p-2 rounded border border-indigo-200 text-[11px] text-indigo-900 flex flex-col gap-1 mb-2">
                          <div className="font-semibold text-indigo-800 mb-1">Staged Data (Ready to Associate)</div>
                          <div className="flex justify-between">
                            <span>ID:</span>
                            <span className="font-medium">{importedData.id}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Name:</span>
                            <span className="font-medium">{importedData.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Type:</span>
                            <span className="font-medium">{importedData.type}</span>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 mb-2">
                        <button
                          disabled={!selectedDBEntry}
                          onClick={() => {
                            if (selectedDBEntry) {
                              setImportedData({
                                id: selectedDBEntry.spaceNumber,
                                name: selectedDBEntry.spaceHeader || "",
                                type: "Space", // default
                                area: drawingSpaceData.area
                              });
                            }
                          }}
                          className={`flex-1 py-1 px-2 rounded text-[11px] font-medium transition ${
                            selectedDBEntry 
                              ? 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200' 
                              : 'bg-slate-50 text-slate-400 border border-slate-200 cursor-not-allowed'
                          }`}
                        >
                          Get Data Base Entry
                        </button>
                        
                        <button
                          disabled={drawingSpaceData.area === -1 || !selectedDBEntry}
                          onClick={() => {
                            if (selectedDBEntry && drawingSpaceData.area !== -1) {
                               const areaInFt = areaUnit === 'in' ? (drawingSpaceData.area / 144).toFixed(2) : drawingSpaceData.area.toFixed(2);
                               const updatedInfo = { ...selectedDBEntry };
                               if (!updatedInfo.summary) updatedInfo.summary = {};
                               updatedInfo.summary['GLA'] = areaInFt;
                               
                               setLocalPlansInfo((prev: any) => {
                                 if (!prev) return prev;
                                 const newSpaces = prev.spaces.map((s: any) => s.spaceNumber === updatedInfo.spaceNumber ? updatedInfo : s);
                                 return { ...prev, spaces: newSpaces };
                               });
                            }
                          }}
                          className={`flex-1 py-1 px-2 rounded text-[11px] font-medium transition ${
                            drawingSpaceData.area !== -1 && selectedDBEntry
                              ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                              : 'bg-slate-50 text-slate-400 border border-slate-200 cursor-not-allowed'
                          }`}
                        >
                          Import Area
                        </button>
                      </div>

                      <button
                        disabled={!importedData}
                        onClick={() => {
                          if (importedData) {
                            try {
                              // @ts-ignore
                              if (cadviewer.cvjs_changeSpaceObjectID) cadviewer.cvjs_changeSpaceObjectID(drawingSpaceData.id, importedData.id);
                              // @ts-ignore
                              if (cadviewer.cvjs_changeSpaceObjectName) cadviewer.cvjs_changeSpaceObjectName(importedData.id, importedData.name);
                              // @ts-ignore
                              if (cadviewer.cvjs_changeSpaceObjectType) cadviewer.cvjs_changeSpaceObjectType(importedData.id, importedData.type);

                              setSelectedSpacesChips([importedData.id]);
                            } catch (e) {
                              console.error("Error associating space:", e);
                            }
                          }
                        }}
                        className={`w-full py-1.5 px-3 rounded text-xs font-semibold shadow-sm transition flex justify-center items-center gap-1.5 ${
                          importedData
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        }`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                        Associate Space
                      </button>
                    </div>
                  )}

                  {selectedDBEntry && (
                    <div className="mt-2 border-t border-slate-200 pt-3">
                      <div className="font-medium text-slate-700 mb-1.5">{selectedDBEntry.spaceHeader} (ID: {selectedDBEntry.spaceNumber}) Overview</div>
                      <div className="bg-white p-2 rounded border border-slate-200 max-h-[120px] overflow-y-auto custom-scrollbar-light text-[11px] text-slate-600 flex flex-col gap-1">
                        {selectedDBEntry.summary && Object.keys(selectedDBEntry.summary).map(key => {
                          const isGreen = key === 'GLA' || key === 'Actual Footprint SF';
                          return (
                            <div key={key} className="flex gap-1 break-words">
                              <span className={`font-semibold ${isGreen ? 'text-emerald-600' : 'text-slate-700'}`}>{key}:</span>
                              <span className={`flex-1 ${isGreen ? 'text-emerald-600 font-medium' : ''}`}>{selectedDBEntry.summary![key]}</span>
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-3 font-medium text-slate-700 mb-1.5">{selectedDBEntry.spaceHeader} (ID: {selectedDBEntry.spaceNumber}) Detailed Information</div>
                      <div className="bg-white rounded border border-slate-200 max-h-[180px] overflow-y-auto custom-scrollbar-light text-[11px] text-slate-600 flex flex-col divide-y divide-slate-100">
                        {selectedDBEntry.detailTabs && (() => {
                          const allDetails = selectedDBEntry.detailTabs.reduce((acc, tab) => {
                             if (tab.detail) return { ...acc, ...tab.detail };
                             return acc;
                          }, {} as Record<string, any>);

                          const keysByGroup: Record<string, string[]> = Object.keys(config.accordionGroups || {}).reduce((acc, group) => {
                            acc[group] = [];
                            return acc;
                          }, {} as Record<string, string[]>);
                          
                          Object.keys(allDetails).forEach(key => {
                            let foundGroup = "Others";
                            for (const [group, keys] of Object.entries(config.accordionGroups || {})) {
                              if ((keys as string[]).includes(key)) {
                                foundGroup = group;
                                break;
                              }
                            }
                            if (!keysByGroup[foundGroup]) keysByGroup[foundGroup] = [];
                            keysByGroup[foundGroup].push(key);
                          });

                          return Object.entries(keysByGroup).filter(([group, keys]) => keys.length > 0).map(([group, keys]) => (
                            <div key={group} className="flex flex-col">
                              <button 
                                onClick={() => toggleDbAccordion(group)}
                                className="flex items-center justify-between p-2 hover:bg-slate-50 transition w-full text-left"
                              >
                                <span className="font-semibold text-slate-700">{group}</span>
                                {dbAccordionState[group] ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
                              </button>
                              {dbAccordionState[group] && (
                                <div className="p-2 bg-slate-50/50 flex flex-col gap-1 border-t border-slate-100">
                                  {keys.map(key => {
                                    const isGreen = key === 'GLA' || key === 'Actual Footprint SF';
                                    return (
                                      <div key={key} className="flex gap-1 break-words px-1">
                                        <span className={`font-semibold ${isGreen ? 'text-emerald-600' : 'text-slate-600'}`}>{key}:</span>
                                        <span className={`flex-1 ${isGreen ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>{allDetails[key]}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  )}
                </div>
                </div>
              )}

              {activePanel === 'lod' && (
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex flex-col gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">LOD Location:</span>
                    <span 
                      className="font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 shadow-sm truncate cursor-pointer hover:bg-indigo-100 hover:text-indigo-800 transition" 
                      title={getLodDisplayUrl()}
                      onClick={() => EventSystem.publish("panel.folder_list.open", {})}
                    >
                      {getLodDisplayUrl()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">Space ID:</span>
                    <input 
                      type="text"
                      value={spaceIdInputValue}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSpaceIdInputValue(val);
                        if (!val.trim()) {
                          setSelectedSpacesChips([]);
                        } else {
                          setSelectedSpacesChips(val.split(',').map(s => s.trim()).filter(Boolean));
                        }
                      }}
                      placeholder="Enter Space ID(s)"
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0 font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">Other Spaces:</span>
                    <input 
                      type="text"
                      value={otherSpacesInputValue}
                      onChange={(e) => setOtherSpacesInputValue(e.target.value)}
                      placeholder=""
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0 font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">Min Dimension Distance:</span>
                    <input 
                      type="text"
                      value={minDimensionDistance}
                      onChange={(e) => setMinDimensionDistance(e.target.value)}
                      placeholder=""
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0 font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">Layer Off List:</span>
                    <input 
                      type="text"
                      value={layerOffList}
                      onChange={(e) => setLayerOffList(e.target.value)}
                      placeholder=""
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0 font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">Space Layer:</span>
                    <select
                      value={lodSpaceLayer || ""}
                      onChange={(e) => setLodSpaceLayer(e.target.value || null)}
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                    >
                      <option value="">-- Select Layer --</option>
                      {selectedLayers.length === 0 && <option disabled>No layers selected</option>}
                      {selectedLayers.map(layerId => (
                        <option key={`space-${layerId}`} value={layerId} disabled={layerId === lodTextLayer}>
                          {layerNamesMap[layerId] || formatLayerName(layerId)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">ID/Text Layer:</span>
                    <select
                      value={lodTextLayer || ""}
                      onChange={(e) => setLodTextLayer(e.target.value || null)}
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                    >
                      <option value="">-- Select Layer --</option>
                      {selectedLayers.map(layerId => (
                        <option key={`text-${layerId}`} value={layerId} disabled={layerId === lodSpaceLayer}>
                          {layerNamesMap[layerId] || formatLayerName(layerId)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">Content line 1:</span>
                    <select
                      value={contentLine1}
                      onChange={(e) => setContentLine1(e.target.value)}
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                    >
                      <option value="">-- Select Content --</option>
                      <option value="none">none</option>
                      <option value="Storefront Name">Storefront Name</option>
                      <option value="Space ID">Space ID</option>
                      <option value="Area">Area</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">Content line 2:</span>
                    <select
                      value={contentLine2}
                      onChange={(e) => setContentLine2(e.target.value)}
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                    >
                      <option value="">-- Select Content --</option>
                      <option value="none">none</option>
                      <option value="Storefront Name">Storefront Name</option>
                      <option value="Space ID">Space ID</option>
                      <option value="Area">Area</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium whitespace-nowrap min-w-[110px]">Content line 3:</span>
                    <select
                      value={contentLine3}
                      onChange={(e) => setContentLine3(e.target.value)}
                      className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                    >
                      <option value="">-- Select Content --</option>
                      <option value="none">none</option>
                      <option value="Storefront Name">Storefront Name</option>
                      <option value="Space ID">Space ID</option>
                      <option value="Area">Area</option>
                    </select>
                  </div>
                </div>
              )}

              {activePanel === 'processSourceDWG' && (
                <div className="flex flex-col">
                  {/* Loaded File Info */}
                  <div 
                    className="px-3 py-2 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition grid grid-cols-[auto_1fr] gap-x-2 gap-y-1.5 items-center shrink-0"
                    onClick={() => EventSystem.publish("panel.folder_list.open", {})}
                    title="Click to open Folder Structure"
                  >
                    <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Loaded File:</span>
                    <div className="overflow-x-auto whitespace-nowrap flex items-center h-full" style={{ scrollbarWidth: 'none' }}>
                      <span className="text-[11px] font-medium text-slate-700">
                        {getLoadedFileName(currentDrawingPath)}
                      </span>
                    </div>
                    
                    <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Version:</span>
                    <div className="flex items-center">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        currentDrawingVersion === 'Current' || currentDrawingVersion === 'LATEST_FROM_TREE' || !currentDrawingVersion 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-indigo-100 text-indigo-700'
                      }`}>
                        {currentDrawingVersion === 'LATEST_FROM_TREE' ? 'Latest' : (currentDrawingVersion || 'Current')}
                      </span>
                    </div>
                  </div>

                  {/* Action Bar */}
                  <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100 bg-white">
                    <button
                      onClick={handleRunServerSideConversion}
                      disabled={!processSpaceLayer}
                      className={`flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border text-xs ${
                        processSpaceLayer 
                          ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm cursor-pointer' 
                          : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      <Sparkles size={14} className={processSpaceLayer ? 'text-indigo-200' : 'text-slate-300'} />
                      <span className="whitespace-nowrap">Run Server Side Conversion</span>
                    </button>
                    
                    <button
                      onClick={() => EventSystem.publish("modal.save_svg.open", {})}
                      className="flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border text-xs border-indigo-600 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 shadow-sm cursor-pointer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
                      <span className="whitespace-nowrap">Save as SVG</span>
                    </button>
                  </div>
                  
                  {/* Dropdowns */}
                  <div className="px-4 py-3 bg-slate-50 flex flex-col gap-3 text-xs border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-medium whitespace-nowrap min-w-[130px]">Space Layer (RL):</span>
                      <select
                        value={processSpaceLayer || ""}
                        onChange={(e) => setProcessSpaceLayer(e.target.value || null)}
                        className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                      >
                        <option value="">-- Select Layer --</option>
                        {selectedLayers.length === 0 && <option disabled>No layers selected</option>}
                        {selectedLayers.map(layerId => (
                          <option key={`ps-${layerId}`} value={layerId} disabled={layerId === processTextLayer}>
                            {layerNamesMap[layerId] || formatLayerName(layerId)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 font-medium whitespace-nowrap min-w-[130px]">Text/ID Layer (TL):</span>
                      <select
                        value={processTextLayer || ""}
                        onChange={(e) => setProcessTextLayer(e.target.value || null)}
                        className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                      >
                        <option value="">-- Select Layer --</option>
                        {selectedLayers.length === 0 && <option disabled>No layers selected</option>}
                        {selectedLayers.map(layerId => (
                          <option key={`pt-${layerId}`} value={layerId} disabled={layerId === processSpaceLayer}>
                            {layerNamesMap[layerId] || formatLayerName(layerId)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Dynamic CLI Parameters */}
                    <div className="pt-3 mt-3 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-600 font-semibold">Other CLI Parameters</span>
                        <button
                          onClick={handleAddParameter}
                          className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 font-medium transition cursor-pointer"
                        >
                          <Plus size={12} />
                          <span>Add Parameter</span>
                        </button>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <datalist id="cli-params-list">
                          {AXparameters.map(ax => (
                            <option key={ax} value={ax} />
                          ))}
                        </datalist>
                        {processParameters.map((param, index) => (
                          <div key={index} className="flex items-center gap-1.5">
                            <input
                              list="cli-params-list"
                              value={param.name}
                              onChange={(e) => handleUpdateParameter(index, 'name', e.target.value)}
                              placeholder="-- Param --"
                              className="w-1/3 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                            />
                            <input
                              type="text"
                              value={param.value}
                              onChange={(e) => handleUpdateParameter(index, 'value', e.target.value)}
                              placeholder="Value (opt.)"
                              className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                            />
                            <button
                              onClick={() => handleRemoveParameter(index)}
                              className="p-1 text-slate-400 hover:text-rose-500 transition rounded hover:bg-rose-50 cursor-pointer"
                              title="Remove parameter"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        {processParameters.length === 0 && (
                          <div className="text-center py-2 text-slate-400 italic">No parameters added.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePanel === 'jsonDataExtraction' && (
                <div className="flex flex-col">
                  {/* Loaded File Info */}
                  <div 
                    className="px-3 py-2 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 transition grid grid-cols-[auto_1fr] gap-x-2 gap-y-1.5 items-center shrink-0"
                    onClick={() => EventSystem.publish("panel.folder_list.open", {})}
                    title="Click to open Folder Structure"
                  >
                    <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Loaded File:</span>
                    <div className="overflow-x-auto whitespace-nowrap flex items-center h-full" style={{ scrollbarWidth: 'none' }}>
                      <span className="text-[11px] font-medium text-slate-700">
                        {getLoadedFileName(currentDrawingPath)}
                      </span>
                    </div>
                  </div>

                  {/* Action Bar */}
                  <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-100 bg-white">
                    <button
                      onClick={handleRunDataExtraction}
                      disabled={actionStatus === "processing_dwg"}
                      className={`flex items-center gap-1.5 font-medium transition py-1 px-3 rounded-md border text-xs ${
                        actionStatus !== "processing_dwg"
                          ? 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm cursor-pointer'
                          : 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      <Sparkles size={14} className={actionStatus !== "processing_dwg" ? 'text-indigo-200' : 'text-slate-300'} />
                      <span className="whitespace-nowrap">{actionStatus === "processing_dwg" ? "Extracting..." : "Run Data Extraction"}</span>
                    </button>
                  </div>
                  
                  {/* Dropdowns */}
                  <div className="px-4 py-3 bg-slate-50 flex flex-col gap-3 text-xs border-b border-slate-100">
                    <div className="pt-1 mt-1">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-600 font-semibold">CLI Parameters (JSON always included)</span>
                        <button
                          onClick={handleAddJsonParameter}
                          className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 font-medium transition cursor-pointer"
                        >
                          <Plus size={12} />
                          <span>Add Parameter</span>
                        </button>
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <datalist id="json-params-list">
                          {["BLOCKS", "ALL", "LAYERS", "TEXT", "MTEXT", "TRACE"].map(ax => (
                            <option key={ax} value={ax} />
                          ))}
                        </datalist>
                        {jsonParameters.map((param, index) => (
                          <div key={index} className="flex items-center gap-1.5">
                            <input
                              list="json-params-list"
                              value={param.name}
                              onChange={(e) => handleUpdateJsonParameter(index, 'name', e.target.value)}
                              placeholder="-- Param --"
                              className="w-1/3 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                            />
                            <input
                              type="text"
                              value={param.value}
                              onChange={(e) => handleUpdateJsonParameter(index, 'value', e.target.value)}
                              placeholder="Value (opt.)"
                              className="flex-1 bg-white border border-slate-300 rounded px-2 py-1 text-slate-700 outline-none focus:border-indigo-500 min-w-0"
                            />
                            <button
                              onClick={() => handleRemoveJsonParameter(index)}
                              className="p-1 text-slate-400 hover:text-rose-500 transition rounded hover:bg-rose-50 cursor-pointer"
                              title="Remove parameter"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {activePanel === 'vizQuerySetup' && (
                <VizQuerySetupModal 
                  isOpen={true}
                  onClose={closePanel}
                />
              )}
              {activePanel === 'applicationSetup' && (
                <ApplicationSetupModal 
                  isOpen={true}
                  onClose={closePanel}
                />
              )}
              {activePanel === 'aiSetup' && (
                <AISetupModal
                  isOpen={true}
                  onClose={closePanel}
                />
              )}
              {activePanel === 'informationLayersSetup' && (
                <ConfigureInformationLayersModal
                  isOpen={true}
                  onClose={closePanel}
                />
              )}

              {/* Empty state with instructions */}
              {!isSetupPanel(activePanel) && activePanel !== 'spaceAssociation' && selectedSpacesChips.length === 0 && multiSelectArray.length === 0 && (
                <div className="px-4 py-4 text-center bg-gray-50 border-t border-gray-100">
                  <div className="mb-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-xs text-gray-600 font-medium mb-3">
                    No spaces selected yet
                  </p>
                  <div className="text-xs text-gray-500 mb-3 text-left space-y-1">
                    {activePanel !== 'lod' ? (
                      <>
                        <p className="flex items-start gap-2">
                          <span className="font-bold" style={{ color: '#0f3557' }}>1.</span>
                          <span>Use <strong>Shift+Click</strong> on the canvas to select spaces</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold" style={{ color: '#0f3557' }}>2.</span>
                          <span>Use Quick Actions for analysis</span>
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="flex items-start gap-2">
                          <span className="font-bold" style={{ color: '#0f3557' }}>1.</span>
                          <span>Use <strong>Shift+Click</strong> on the canvas to select a space</span>
                        </p>
                        <p className="flex items-start gap-2">
                          <span className="font-bold" style={{ color: '#0f3557' }}>2.</span>
                          <span>Use Quick Actions for LOD analysis</span>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Result Modal */}
      <ResultModal
        isOpen={resultModal.isOpen}
        onClose={handleCloseResultModal}
        title={resultModal.title}
        message={resultModal.message}
        tableData={resultModal.tableData}
      />

      <JsonEditorModal
        isOpen={isJsonDataEditorOpen}
        onClose={() => setIsJsonDataEditorOpen(false)}
        title={extractedJsonFilename}
        configData={extractedJsonData}
        setConfigData={setExtractedJsonData}
        onSave={(saveAsNewVersion, versionName) => {
          console.log("JSON save requested:", saveAsNewVersion, versionName);
          // Future functionality can be placed here
        }}
        readOnly={false}
        allowVersioning={false}
      />
      
      <FolderSelectModal
        isOpen={isJsonFileSelectOpen}
        onClose={() => setIsJsonFileSelectOpen(false)}
        onSelect={(path) => {
          setSelectedJsonFileUrl(path);
          fetchLocalJson(path);
        }}
        folderStructure={filteredFolderStructure}
        selectType="file"
      />
    </>
  );
};

export default SpaceSelectionPanel;
